import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useMatch } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../constants';
import { Breadcrumb } from '../common';
import { formatUserDisplayName, getUserInitials } from '../../utils/authUtils';

const getRoleBadge = roleRaw => {
  if (!roleRaw) return null;
  const str = String(roleRaw).trim();
  if (
    str === 'SA' ||
    str === 'super_admin' ||
    str.toLowerCase() === 'super administrator' ||
    str.toLowerCase() === 'super admin'
  ) {
    return {
      code: 'SA',
      label: 'Super Administrator',
      badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
      icon: (
        <svg className='w-3.5 h-3.5 mr-1 text-amber-600 shrink-0' fill='currentColor' viewBox='0 0 20 20'>
          <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
        </svg>
      ),
    };
  }
  if (
    str === 'A' ||
    str === 'admin' ||
    str.toLowerCase() === 'administrator'
  ) {
    return {
      code: 'A',
      label: 'Administrator',
      badgeClass: 'bg-purple-50 text-purple-800 border-purple-200',
      icon: (
        <svg className='w-3.5 h-3.5 mr-1 text-purple-600 shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
        </svg>
      ),
    };
  }
  if (
    str === 'P' ||
    str === 'operation' ||
    str.toLowerCase() === 'operation' ||
    str.toLowerCase() === 'operations manager'
  ) {
    return {
      code: 'P',
      label: 'Operations Manager',
      badgeClass: 'bg-blue-50 text-blue-800 border-blue-200',
      icon: null,
    };
  }
  if (
    str === 'M' ||
    str === 'marketing' ||
    str.toLowerCase() === 'marketing' ||
    str.toLowerCase() === 'marketing manager'
  ) {
    return {
      code: 'M',
      label: 'Marketing Manager',
      badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      icon: null,
    };
  }
  if (
    str === 'S' ||
    str === 'staff' ||
    str.toLowerCase() === 'staff' ||
    str.toLowerCase() === 'staff member'
  ) {
    return {
      code: 'S',
      label: 'Staff Member',
      badgeClass: 'bg-gray-50 text-gray-800 border-gray-200',
      icon: null,
    };
  }
  return {
    code: str,
    label: str,
    badgeClass: 'bg-gray-50 text-gray-700 border-gray-200',
    icon: null,
  };
};

const getUserRolesList = user => {
  if (!user) return [];
  const roles = [];
  const seen = new Set();

  // 1. From API user.roles structure: e.g. { admin: ['SA'] }
  if (user.roles && typeof user.roles === 'object') {
    Object.keys(user.roles).forEach(cat => {
      const arr = user.roles[cat];
      if (Array.isArray(arr)) {
        arr.forEach(roleItem => {
          if (roleItem && !seen.has(roleItem)) {
            seen.add(roleItem);
            const badge = getRoleBadge(roleItem);
            if (badge) roles.push(badge);
          }
        });
      }
    });
  }

  // 2. Fallback to user.role if roles was empty
  if (roles.length === 0 && user.role) {
    const badge = getRoleBadge(user.role);
    if (badge) roles.push(badge);
  }

  return roles;
};

const Header = ({ onMenuToggle, isMenuOpen = false }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const isPurchasesPage = useMatch(`${ROUTES.PURCHASES}/*`);
  const userMenuRef = useRef(null);

  // Get auth context
  const { user, logout, isLoggingOut } = useAuth();
  const userRoles = getUserRolesList(user);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = async () => {
    setIsUserMenuOpen(false);

    // Use AuthContext logout function instead of manual localStorage handling
    const result = await logout();

    if (result.success) {
      // Navigate to login page after successful logout
      navigate(ROUTES.LOGIN);
    }
  };

  return (
    <header className='bg-white shadow-sm border-b border-gray-200'>
      <div className='flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8'>
        {/* Left section: Hamburger menu (mobile) and Breadcrumbs (desktop) */}
        <div className='flex items-center'>
          {/* Hamburger menu for mobile */}
          <button
            type='button'
            className='inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden'
            onClick={onMenuToggle}
            aria-expanded='false'
          >
            <span className='sr-only'>Open main menu</span>
            {/* Hamburger icon */}
            {!isMenuOpen ? (
              <svg
                className='block h-6 w-6'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 6h16M4 12h16M4 18h16'
                />
              </svg>
            ) : (
              <svg
                className='block h-6 w-6'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            )}
          </button>

          {/* Breadcrumbs for desktop */}
          {!isPurchasesPage && (
            <div className='hidden lg:block ml-4'>
              <Breadcrumb />
            </div>
          )}
        </div>

        {/* Right section: User menu */}
        <div className='flex items-center'>
          {/* User menu dropdown */}
          <div className='relative' ref={userMenuRef}>
            <button
              type='button'
              className='flex items-center min-w-0 max-w-xs sm:max-w-md bg-white text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 py-1 px-1.5 hover:bg-gray-50 transition-colors'
              onClick={handleUserMenuToggle}
              aria-expanded={isUserMenuOpen}
              aria-haspopup='true'
            >
              <span className='sr-only'>Open user menu</span>
              {user?.avatar ? (
                <img
                  className='w-8 h-8 rounded-full object-cover'
                  src={user.avatar}
                  alt={formatUserDisplayName(user)}
                />
              ) : (
                <div className='w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shrink-0'>
                  <span className='text-white text-sm font-medium'>
                    {getUserInitials(user)}
                  </span>
                </div>
              )}
              <span className='ml-2 text-sm font-medium text-gray-700 hidden sm:block truncate max-w-56'>
                {formatUserDisplayName(user)}
              </span>
              <svg
                className='ml-1.5 h-4 w-4 text-gray-400 shrink-0'
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 20 20'
                fill='currentColor'
                aria-hidden='true'
              >
                <path
                  fillRule='evenodd'
                  d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                  clipRule='evenodd'
                />
              </svg>
            </button>

            {/* User dropdown menu */}
            {isUserMenuOpen && (
              <div className='origin-top-right absolute right-0 mt-2 min-w-64 max-w-sm w-max rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50'>
                <div className='py-1' role='menu' aria-orientation='vertical'>
                  {/* User Info Section */}
                  <div className='px-4 py-3 border-b border-gray-100'>
                    <p className='text-sm font-semibold text-gray-900 truncate'>
                      {formatUserDisplayName(user)}
                    </p>
                    <p className='text-xs text-gray-500 break-all mt-0.5'>{user?.email}</p>
                    {user?.phone && (
                      <p className='text-2xs text-gray-400 mt-0.5'>{user.phone}</p>
                    )}
                    {userRoles.length > 0 && (
                      <div className='mt-2 flex flex-wrap gap-1.5'>
                        {userRoles.map((r, idx) => (
                          <span
                            key={r.code || idx}
                            className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full border shadow-2xs ${r.badgeClass}`}
                          >
                            {r.icon}
                            {r.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Navigation Links */}
                  <Link
                    to={ROUTES.PROFILE}
                    className='flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition duration-150'
                    role='menuitem'
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <svg
                      className='mr-3 h-4 w-4 text-gray-400'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                      />
                    </svg>
                    Your Profile
                  </Link>

                  <Link
                    to={ROUTES.PROFILE}
                    state={{ activeTab: 'password' }}
                    className='flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition duration-150'
                    role='menuitem'
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <svg
                      className='mr-3 h-4 w-4 text-gray-400'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                      />
                    </svg>
                    Change Password
                  </Link>
                  <div className='border-t border-gray-100'></div>
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isLoggingOut ? 'bg-gray-50' : ''
                    }`}
                    role='menuitem'
                  >
                    {isLoggingOut ? (
                      <>
                        <div className='mr-3 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600'></div>
                        Signing out...
                      </>
                    ) : (
                      <>
                        <svg
                          className='mr-3 h-4 w-4 text-gray-400'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
                          />
                        </svg>
                        Sign out
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
